import React, { useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash';
import queryString from 'query-string';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import { makeStyles, useTheme } from '@material-ui/core';
import Hidden from '@material-ui/core/Hidden';
import TablePagination from '@material-ui/core/TablePagination';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { loadAccountApplicationsByStatus } from './actions/load-account-applications-by-status';
import { loadStatusChangeReasons } from '../action/application/load-status-change-reasons';
import { loadOrgs } from '../action/load-orgs';
import { loadOrgApplicationsByStatus } from '../application/actions/load-org-applications-by-status';
import { loadAccountJobs } from '../job/actions/load-account-jobs';
import {
  statusChangeReasonsSelector,
  applicationsStateForAccountIdAndStatusSelector,
  fullApplicationsForAccountIdSelector,
  applicationsStateForOrgIdAndStatusSelector,
} from '../reducers/application-selectors';
import { userSelector } from '../reducers/current-user-selectors';
import { accountJobsSelector } from '../reducers/job-selectors';
import { orgsForAccountId } from '../reducers/org-selectors';
import { RequestState } from '../reducers/utils/state-manager';

import { ApplicationPopUp } from '../application/application-pop-up';
import { UiBreadcrumbHeader } from '../ui/ui-breadcrumb-header';
import { UiSortLabel } from '../ui/ui-sort-label';
import {
  jobInProgressRoute,
  jobNeedReviewRoute,
  applicantDirectoryRoute,
  HOME_ROUTE,
} from '../web-routes';

import { ApplicantDirectoryCard } from './cards/applicant-directory-card';

import {
  ApplicantDirectoryQueryParams,
  groupedStatusChangeReasons,
  isApplicationStatus,
  validateApplicantDirectoryQueryParams,
} from './utils/applicant-directory-utils';
import { getPreviousLocation } from './utils/get-previous-location';
import { AddApplicantDialog } from './widgets/add-applicant-dialog';
import { ApplicantDirectoryEmptyState } from './widgets/applicant-directory-empty-state';
import { ApplicantFilters } from './widgets/applicant-filters';
import { ExportApplicantsDialog } from './widgets/export-applicants-dialog';
import { MoveApplicantDialog } from '../application/widgets/move-applicant-dialog';
import { ShareApplicantDialog } from '../application/widgets/share-applicant-dialog';
import {
  APPLIED_STATUS,
  STATUSES_EXCLUDED_BY_DEFAULT,
  STATUSES_TO_DISPLAY,
  statusDisplay,
} from '../config/application-status-config';
import { SOURCE_TYPES } from '../util/application-source-types';
import { HeaderButtonAddIcon, ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';
import { EMPLOYEE_PERMISSION } from '../util/permissions';
import { sortAlphabetically, SORT_ORDER } from '../util/sort-functions';
import { trackWithSmartlook } from '../util/track-with-smartlook';
import { LoadingMessage } from '../widgets/loading-message';

type ApplicantDirectoryQueryParamsUpdate = {
  updatedFilterByOrgId?: string;
  updatedFilterByNameOrPhoneNumber?: string;
  updatedFilterByStatus?: ApplicationStatus | '';
  updatedFilterByJobId?: string;
  updatedFilterByLocation?: string;
  updatedFilterByReason?: string;
  updatedIncludeRejectedExpired?: boolean;
  updatedSortBy?: string;
  updatedSortDirection?: 'asc' | 'desc';
};

type InputProps = {
  accountId: number;
  lastVisitedOrgId: number;
};

const gridBase = { display: 'grid', width: '100%', gap: '10px' };
const useStyles = makeStyles(() => ({
  gridContainerLg: {
    gridTemplateColumns: 'repeat(6, 2fr) 3fr 1fr',
    ...gridBase,
  },
  gridContainerMd: {
    gridTemplateColumns: 'repeat(7, 3fr) 1fr',
    ...gridBase,
  },
  gridContainerSm: {
    gridTemplateColumns: 'repeat(3, 2fr) 1fr',
    ...gridBase,
  },
  gridContainerXs: {
    gridTemplateColumns: '3fr 3fr 1fr',
    ...gridBase,
  },
}));

export const ApplicantDirectory = ({ accountId, lastVisitedOrgId }: InputProps) => {
  const classes = useStyles();
  const user = useSelector((state: any) => userSelector(state), shallowEqual);
  const orgs = useSelector((state: any) => orgsForAccountId(state, accountId), shallowEqual);
  const applicants = useSelector(
    (state: any) => fullApplicationsForAccountIdSelector(state, accountId),
    shallowEqual,
  );
  const jobs = useSelector((state: any) => accountJobsSelector(state, accountId), shallowEqual);
  const statusChangeReasons = useSelector(
    (state: any) => statusChangeReasonsSelector(state),
    shallowEqual,
  );

  const { search } = useLocation();
  const queryParams = validateApplicantDirectoryQueryParams(queryString.parse(search));
  const {
    filterByOrgId: orgFilter,
    filterByJobId: jobFilter,
    filterByNameOrPhoneNumber: nameFilter,
    filterByStatus: statusFilter,
    filterByReason: availabilityFilter,
    filterByLocation: locationFilter,
    includeRejectedExpired: rejectedExpiredFilter,
  } = queryParams;

  const [filterByNameOrPhoneNumber, setFilterByNameOrPhoneNumber] = useState<string>(
    queryParams.filterByNameOrPhoneNumber || '',
  );
  const filterByOrgId = orgFilter;
  const filterByJobId = jobFilter || '';
  const filterByLocation = locationFilter || '';
  const filterByStatus = statusFilter || '';
  const filterByReason = availabilityFilter || '';
  const includeRejectedExpired = rejectedExpiredFilter;

  const [exportApplicantDialogVisible, setExportApplicantDialogVisible] = useState<boolean>(false);
  const [addApplicantDialogVisible, setAddApplicantDialogVisible] = useState<boolean>(false);
  const [shareApplicantDialogVisible, setShareApplicantDialogVisible] = useState<boolean>(false);
  const [moveApplicantDialogVisible, setMoveApplicantDialogVisible] = useState<boolean>(false);
  const [applicantProfileVisible, setApplicantProfileVisible] = useState<boolean>(false);
  const [selectedApplicantId, setSelectedApplicantId] = useState<number | null>(null);
  const [selectedApplicantJobId, setSelectedApplicantJobId] = useState<number | null>(null);
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const pageSize = 20;
  const [offset, setOffset] = useState<number>(0);

  const sortBy = queryParams?.sortBy || 'name';
  const sortDirection = queryParams?.sortDirection || 'asc';

  const history = useHistory();
  const previousLocationPath = queryParams.previousLocationPath || HOME_ROUTE;

  const previousLocation =
    queryParams.previousLocation ||
    getPreviousLocation(previousLocationPath.split('?')[0], {
      user,
      orgId: lastVisitedOrgId,
    });

  // Making all the filters uniform and large in the grid looks nicer as the screen size varies
  // But there's a transition area where the larger filter boxes overlap we have to account for
  const isMediumScreen = useMediaQuery('(min-width: 950px)');
  const isLargeScreen = useMediaQuery('(min-width: 1200px)');
  const isXLargeScreen = useMediaQuery('(min-width: 1340px)');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isXSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const filterWidth =
    (isMediumScreen && !isLargeScreen) || (isLargeScreen && !isXLargeScreen)
      ? 'u-width-150px'
      : isXSmallScreen
      ? 'u-width-100'
      : 'u-width-175px';

  // The grid requires slightly different breakpoints to not be visibly cluttered
  const gridIsMediumScreen = useMediaQuery('(max-width: 1150px)') && !isSmallScreen;
  let gridContainerClass = classes.gridContainerLg;
  if (isSmallScreen) {
    gridContainerClass = classes.gridContainerSm;
  }
  if (gridIsMediumScreen) {
    gridContainerClass = classes.gridContainerMd;
  }
  if (isXSmallScreen) {
    gridContainerClass = classes.gridContainerXs;
  }

  const sendSmartLookInformation = (updatedValues: ApplicantDirectoryQueryParamsUpdate) => {
    const smartlookEventInformation = {
      eventName: 'Applicant Search Submit',
      props: {
        filterByNameOrPhoneNumber:
          updatedValues.updatedFilterByNameOrPhoneNumber ?? filterByNameOrPhoneNumber,
        filterByStatus: updatedValues.updatedFilterByStatus ?? filterByStatus,
        filterByJobId: updatedValues.updatedFilterByJobId ?? filterByJobId,
        filterByLocation: updatedValues.updatedFilterByLocation ?? filterByLocation,
        filterByReason: updatedValues.updatedFilterByReason ?? filterByReason,
        includeRejectedExpired: !!(
          updatedValues.updatedIncludeRejectedExpired ?? includeRejectedExpired
        ),
      },
    };

    trackWithSmartlook(smartlookEventInformation);
  };

  // We never want the debounce reference to changes so we don't need to watch dependencies
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sendToSmartLook = useMemo(() => debounce(sendSmartLookInformation, 1000), []);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(loadOrgs(accountId));
    dispatch(loadAccountJobs(accountId));
    dispatch(loadStatusChangeReasons());

    if (filterByOrgId) {
      dispatch(loadOrgApplicationsByStatus(parseInt(filterByOrgId, 10), STATUSES_TO_DISPLAY));
    } else {
      dispatch(loadAccountApplicationsByStatus(accountId, STATUSES_TO_DISPLAY));
    }

    // Only load rejected and expired applicants if necessary
    if (
      includeRejectedExpired ||
      (filterByStatus && STATUSES_EXCLUDED_BY_DEFAULT.includes(filterByStatus))
    ) {
      if (filterByOrgId) {
        dispatch(
          loadOrgApplicationsByStatus(parseInt(filterByOrgId, 10), STATUSES_EXCLUDED_BY_DEFAULT),
        );
      } else {
        dispatch(loadAccountApplicationsByStatus(accountId, STATUSES_EXCLUDED_BY_DEFAULT));
      }
    }
  });

  const changeReasonStatusesToExclude = includeRejectedExpired ? [] : STATUSES_EXCLUDED_BY_DEFAULT;
  const groupedStatusReasons = groupedStatusChangeReasons(
    statusChangeReasons || [],
    changeReasonStatusesToExclude,
  );

  const openApplicantProfile = (applicantId: number, tab: string) => () => {
    setSelectedApplicantId(applicantId);
    setSelectedTab(tab);
    setApplicantProfileVisible(true);
  };

  const closeDetailsDialog = () => {
    setSelectedApplicantId(null);
    setSelectedTab(null);
    setApplicantProfileVisible(false);
  };

  const toggleMoveApplicantDialog = (applicantId: number | null) => () => {
    setSelectedApplicantId(applicantId);
    setMoveApplicantDialogVisible(!moveApplicantDialogVisible);
  };

  const toggleShareApplicantDialog = (applicantId: number | null, jobId: number | null) => () => {
    setSelectedApplicantJobId(jobId);
    setSelectedApplicantId(applicantId);
    setShareApplicantDialogVisible(!shareApplicantDialogVisible);
  };

  const routeOffOfPage = (path: string) => {
    // since we don't update the queryParams unless a non name filter is changed it is possible to not capture the last view correctly
    // if a user's last action was to change the name filter, if they were to navigate away and return the search box would not reflect their last search
    // update the params one last time to properly reflect the queryParams
    handleFilterChanged({});
    history.push(path);
  };

  const handleViewInFlowClick = (applicant: FullApplicant) => () => {
    const {
      job_id: jobId,
      job_applicant_id: jobApplicantId,
      status,
      orgId: applicantOrgId,
    } = applicant;

    if (status === APPLIED_STATUS) {
      routeOffOfPage(jobNeedReviewRoute(applicantOrgId, jobId, { jobApplicantId }));
    } else {
      routeOffOfPage(jobInProgressRoute(applicantOrgId, jobId, { jobApplicantId, status }));
    }
  };

  const toggleAddApplicantDialog = () => {
    setAddApplicantDialogVisible(!addApplicantDialogVisible);
  };

  const toggleExportApplicantDialog = () => {
    setExportApplicantDialogVisible(!exportApplicantDialogVisible);
  };

  const updateSearchTerm = (updatedFilterByNameOrPhoneNumber: string) => {
    setFilterByNameOrPhoneNumber(updatedFilterByNameOrPhoneNumber);

    sendToSmartLook({ updatedFilterByNameOrPhoneNumber });
  };

  const handleFilterByOrgIdChange = (updatedFilterByOrgId: string) => {
    if (!updatedFilterByOrgId) {
      // if we're clearing the org filter just update that param
      handleFilterChanged({ updatedFilterByOrgId });
    } else {
      // if we're changing the org to another org, clear the job and location filter because different orgs have different jobs
      handleFilterChanged({
        updatedFilterByOrgId,
        updatedFilterByJobId: '',
        updatedFilterByLocation: '',
      });
    }
  };

  const handleFilterByJobIdChange = (updatedFilterByJobId: string) => {
    handleFilterChanged({ updatedFilterByJobId });
  };

  const handleFilterByLocationChange = (updatedFilterByLocation: string) => {
    handleFilterChanged({ updatedFilterByLocation });
  };

  const handleFilterByReasonChange = (updatedFilterByReason: string) => {
    handleFilterChanged({ updatedFilterByReason });
  };

  const handleFilterByStatusChange = (updatedFilterByStatus: string) => {
    let newStatusFilter: ApplicationStatus | '' | undefined;
    if (isApplicationStatus(updatedFilterByStatus) || updatedFilterByStatus === '') {
      newStatusFilter = updatedFilterByStatus;
    }
    handleFilterChanged({ updatedFilterByStatus: newStatusFilter });
  };

  const handleIncludeRejectedExpiredChange = () => {
    const updatedIncludeRejectedExpired = !includeRejectedExpired;
    const isRejectedExpiredStatusFilter =
      filterByStatus && STATUSES_EXCLUDED_BY_DEFAULT.includes(filterByStatus);
    const isRejectedExpiredReasonFilter = !!statusChangeReasons?.find(
      (scr) =>
        scr.reasonDescription === filterByReason &&
        STATUSES_EXCLUDED_BY_DEFAULT.includes(scr.toStatus),
    );

    handleFilterChanged({
      updatedIncludeRejectedExpired,
      // if we're excluding rejected/expired we want to reset the filter from those statuses
      updatedFilterByStatus:
        !updatedIncludeRejectedExpired && isRejectedExpiredStatusFilter ? '' : filterByStatus,
      // and the filter for change reasons
      updatedFilterByReason:
        !updatedIncludeRejectedExpired && isRejectedExpiredReasonFilter ? '' : filterByReason,
    });
  };

  const handleOnSortChange = (updatedSortBy: string) => {
    if (sortBy !== updatedSortBy) {
      handleFilterChanged({ updatedSortBy, updatedSortDirection: 'asc' });
    }
  };

  const handleOnSortDirectionChange = (updatedSortDirection: 'asc' | 'desc') => {
    if (sortDirection !== updatedSortDirection) {
      handleFilterChanged({ updatedSortDirection });
    }
  };

  const sortFunction = (applicant: FullApplicant) => {
    switch (sortBy) {
      case 'name':
        return `${applicant.firstName} ${applicant.lastName}`;
      case 'firstName':
        return applicant.firstName;
      case 'lastName':
        return applicant.lastName;
      case 'phoneNumber':
        return applicant.phoneNumber || '';
      case 'org':
        return applicant.orgName;
      case 'job':
        return applicant.jobTitle || '';
      case 'location':
        return applicant.jobLocation || '';
      case 'status':
        return statusDisplay(applicant.status) || '';
      case 'statusChangeReason':
        return applicant.savedReason || applicant.rejectionReason || '';
      default:
        return `${applicant.firstName} ${applicant.lastName}`;
    }
  };

  // This uses the same method as the employee directory for filtering:
  //    In order to easily share views, filters are added as queryParams and the url is updated on change without page reload
  //    However, for text input fields (filterByNameOrPhoneNumber) replacing history causes the input to lose focus, so you can only add a letter at a time
  //    Instead, we will only update the filterByNameOrPhoneNumber param when another filter is updated and changes to filterByNameOrPhoneNumber will be stored in state
  const handleFilterChanged = (
    updatedFilters: Omit<ApplicantDirectoryQueryParamsUpdate, 'updatedFilterByNameOrPhoneNumber'>,
  ) => {
    const {
      updatedFilterByOrgId,
      updatedFilterByStatus,
      updatedFilterByJobId,
      updatedFilterByLocation,
      updatedFilterByReason,
      updatedIncludeRejectedExpired,
      updatedSortBy,
      updatedSortDirection,
    } = updatedFilters;
    // we only want to see this filter in the queryString if we are including rejected/expired applicants
    // while functionally a boolean, queryParams are really strings so we will pass through "true" when true and undefined when false
    const parsedIncludeRejectedExpired = updatedIncludeRejectedExpired ? 'true' : undefined;
    const updatedQueryParams: ApplicantDirectoryQueryParams = {
      filterByNameOrPhoneNumber,
      filterByOrgId: updatedFilterByOrgId ?? filterByOrgId,
      filterByStatus: (updatedFilterByStatus ?? filterByStatus) || undefined,
      filterByJobId: updatedFilterByJobId ?? filterByJobId,
      filterByLocation: updatedFilterByLocation ?? filterByLocation,
      filterByReason: updatedFilterByReason ?? filterByReason,
      includeRejectedExpired:
        updatedIncludeRejectedExpired === undefined
          ? includeRejectedExpired
          : parsedIncludeRejectedExpired,
      sortBy: updatedSortBy ?? sortBy,
      sortDirection: updatedSortDirection ?? sortDirection,
      previousLocation,
      previousLocationPath,
      accountId,
      orgId: lastVisitedOrgId,
    };

    sendSmartLookInformation(updatedFilters);

    history.replace(applicantDirectoryRoute(updatedQueryParams));
  };

  const changePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number,
  ) => {
    setOffset(page * pageSize);
  };

  const filterApplicants = () => {
    let result = applicants || [];
    if (filterByStatus) {
      result = result.filter((a) => a.status === filterByStatus);
    }
    if (filterByOrgId) {
      const parsedOrgFilter = parseInt(filterByOrgId, 10);
      result = result.filter((a) => a.orgId === parsedOrgFilter);
    }
    if (filterByJobId) {
      const parsedJobFilter = parseInt(filterByJobId, 10);
      result = result.filter((a) => a.job_id === parsedJobFilter);
    }
    if (filterByLocation) {
      result = result.filter((a) => `${a.jobLocation.toUpperCase()}` === filterByLocation);
    }
    if (filterByReason) {
      const reasonIds = groupedStatusReasons[filterByReason];
      result = result.filter(
        (a) => a.statusChangeReasonId && reasonIds?.includes(a.statusChangeReasonId),
      );
    }
    if (filterByNameOrPhoneNumber) {
      // remove spaces and special characters commonly seen in phone numbers: '.', '(', ')', '-'
      const searchTerm = filterByNameOrPhoneNumber.replace(/\W/g, '').toLowerCase();
      result = result.filter((a) => {
        const name = `${a.firstName}${a.lastName}`.toLowerCase().replace(/\W/g, '');
        const phoneNumber = a.phoneNumber?.replace(/\W/g, '') || '';
        return name.includes(searchTerm) || phoneNumber.includes(searchTerm);
      });
    }
    if (!includeRejectedExpired) {
      // Once we load rejected/expired applicants the first time they will remain in redux so we will have to filter them out if the include toggle is flipped
      result = result.filter((a) => !STATUSES_EXCLUDED_BY_DEFAULT.includes(a.status));
    }

    return result;
  };

  const hasFiltersActive =
    orgFilter ||
    jobFilter ||
    nameFilter ||
    statusFilter ||
    availabilityFilter ||
    locationFilter ||
    rejectedExpiredFilter;

  const applicationsAreLoading = useSelector(
    (state: any) =>
      STATUSES_TO_DISPLAY.map((s: string) =>
        filterByOrgId
          ? applicationsStateForOrgIdAndStatusSelector(state, parseInt(filterByOrgId, 10), s)
          : applicationsStateForAccountIdAndStatusSelector(state, accountId, s),
      ).every((s: RequestState) => !s || s.loading),
    shallowEqual,
  );

  if (!applicants || (applicationsAreLoading && !applicants.length)) {
    return <LoadingMessage />;
  }
  const filteredApplicants = filterApplicants().sort(
    sortAlphabetically(sortFunction, sortDirection === 'asc' ? SORT_ORDER.ASC : SORT_ORDER.DESC),
  );
  const paginatedApplicants =
    filteredApplicants.length > pageSize
      ? filteredApplicants.slice(offset, offset + pageSize)
      : filteredApplicants;

  // For the applicant export, use the orgId filter if there is one, if not check the orgId on the jobFilter
  // If there neither filter is selected, the user will have to pick an org in the export dialog
  const jobOrgId = filterByJobId
    ? jobs?.find((j) => j.job_id.toString() === filterByJobId)?.orgId
    : undefined;
  const orgIdForExport = filterByOrgId ? parseInt(filterByOrgId, 10) : jobOrgId;

  const accountHasMultipleOrgs = !!(orgs?.length && orgs.length > 1);
  const exportDisabled = !filteredApplicants.filter(
    (a: FullApplicant) => a.source !== SOURCE_TYPES.DEMO,
  ).length;

  return (
    <div className="u-margin-bottom-clearChat">
      <UiBreadcrumbHeader
        pageTitle="Search Applicants"
        primaryActions={[
          {
            name: 'Add Applicant',
            handler: toggleAddApplicantDialog,
            Icon: <HeaderButtonAddIcon />,
          },
        ]}
        secondaryAction={{
          name: 'Export Applicants',
          handler: toggleExportApplicantDialog,
          disabled: exportDisabled,
          Icon: <MaterialDesignIcon iconName={ICON_NAMES.DOWNLOAD} className="u-padding-top-xs" />,
        }}
        backToPage={previousLocationPath}
      />
      <div className=" u-padding-2x u-padding-bottom-clearChat">
        <ApplicantFilters
          variant={isLargeScreen || isXLargeScreen ? 'large' : 'small'}
          filterWidth={filterWidth}
          buttonWidth={isLargeScreen && !isXLargeScreen ? 'u-width-175px' : filterWidth}
          jobs={jobs || []}
          orgs={orgs || []}
          filterValues={{
            filterByNameOrPhoneNumber,
            filterByOrgId,
            filterByJobId,
            filterByLocation,
            filterByStatus,
            filterByReason,
            includeRejectedExpired: includeRejectedExpired === 'true',
          }}
          filterHandlers={{
            updateSearchTerm,
            handleFilterByOrgIdChange,
            handleFilterByJobIdChange,
            handleFilterByLocationChange,
            handleFilterByStatusChange,
            handleFilterByReasonChange,
            handleIncludeRejectedExpiredChange,
          }}
          fieldsDisabled={!applicants.length}
          statusChangeReasons={groupedStatusReasons}
        />
        {filteredApplicants.length === 0 ? (
          <ApplicantDirectoryEmptyState
            hasFiltersActive={!!hasFiltersActive && !!applicants.length}
            orgId={lastVisitedOrgId}
            hasJobs={!!jobs?.length}
            hasEmployeePermissions={user.employeesPermission === EMPLOYEE_PERMISSION.ALL}
          />
        ) : (
          <div className="card">
            <Hidden smDown>
              <div className="u-padding-lg">
                <div className={gridContainerClass}>
                  <div>
                    <UiSortLabel
                      label="First Name"
                      sortKey="firstName"
                      currentSortBy={sortBy}
                      currentSortDirection={sortDirection}
                      onSortByChange={handleOnSortChange}
                      onSortDirectionChange={handleOnSortDirectionChange}
                    />
                  </div>
                  <div>
                    <UiSortLabel
                      label="Last Name"
                      sortKey="lastName"
                      currentSortBy={sortBy}
                      currentSortDirection={sortDirection}
                      onSortByChange={handleOnSortChange}
                      onSortDirectionChange={handleOnSortDirectionChange}
                    />
                  </div>
                  <div>
                    <UiSortLabel
                      label="Phone Number"
                      sortKey="phoneNumber"
                      currentSortBy={sortBy}
                      currentSortDirection={sortDirection}
                      onSortByChange={handleOnSortChange}
                      onSortDirectionChange={handleOnSortDirectionChange}
                    />
                  </div>
                  {accountHasMultipleOrgs && (
                    <div>
                      <UiSortLabel
                        label="Organization"
                        sortKey="org"
                        currentSortBy={sortBy}
                        currentSortDirection={sortDirection}
                        onSortByChange={handleOnSortChange}
                        onSortDirectionChange={handleOnSortDirectionChange}
                      />
                    </div>
                  )}
                  <div>
                    <UiSortLabel
                      label="Job"
                      sortKey="job"
                      currentSortBy={sortBy}
                      currentSortDirection={sortDirection}
                      onSortByChange={handleOnSortChange}
                      onSortDirectionChange={handleOnSortDirectionChange}
                    />
                  </div>
                  {!accountHasMultipleOrgs && (
                    <div>
                      <UiSortLabel
                        label="Location"
                        sortKey="location"
                        currentSortBy={sortBy}
                        currentSortDirection={sortDirection}
                        onSortByChange={handleOnSortChange}
                        onSortDirectionChange={handleOnSortDirectionChange}
                      />
                    </div>
                  )}
                  <div>
                    <UiSortLabel
                      label="Status"
                      sortKey="status"
                      currentSortBy={sortBy}
                      currentSortDirection={sortDirection}
                      onSortByChange={handleOnSortChange}
                      onSortDirectionChange={handleOnSortDirectionChange}
                    />
                  </div>
                  <div>
                    <UiSortLabel
                      label="Status Reason"
                      sortKey="statusChangeReason"
                      currentSortBy={sortBy}
                      currentSortDirection={sortDirection}
                      onSortByChange={handleOnSortChange}
                      onSortDirectionChange={handleOnSortDirectionChange}
                    />
                  </div>
                  <div className={gridIsMediumScreen || isXSmallScreen ? '' : 'u-width-125px'} />
                </div>
              </div>
            </Hidden>
            {paginatedApplicants.map((a: FullApplicant) => (
              <ApplicantDirectoryCard
                key={`applicant-directory-applicant-${a.job_applicant_id}`}
                applicant={a}
                accountHasMultipleOrgs={accountHasMultipleOrgs}
                openProfile={openApplicantProfile(a.job_applicant_id, 'DETAILS')}
                messageApplicant={openApplicantProfile(a.job_applicant_id, 'MESSAGES')}
                shareApplicant={toggleShareApplicantDialog(a.job_applicant_id, a.job_id)}
                moveApplicant={toggleMoveApplicantDialog(a.job_applicant_id)}
                viewInFlow={handleViewInFlowClick(a)}
                gridContainerClass={gridContainerClass}
                screenSizes={{
                  isXSmallScreen,
                  isSmallScreen,
                  isMediumScreen: gridIsMediumScreen,
                }}
              />
            ))}
          </div>
        )}
        {filteredApplicants.length >= pageSize && (
          <TablePagination
            component="div"
            count={filteredApplicants.length}
            rowsPerPage={pageSize}
            page={offset / pageSize}
            backIconButtonProps={{
              'aria-label': 'Previous Page',
            }}
            nextIconButtonProps={{
              'aria-label': 'Next Page',
            }}
            onPageChange={changePage}
            rowsPerPageOptions={[pageSize]}
          />
        )}
        {selectedApplicantId ? (
          <>
            {applicantProfileVisible && (
              <ApplicationPopUp
                key={selectedApplicantId}
                jobApplicantId={selectedApplicantId}
                close={closeDetailsDialog}
                selectedTab={selectedTab}
              />
            )}
            {moveApplicantDialogVisible && (
              <MoveApplicantDialog
                key={selectedApplicantId}
                onClose={toggleMoveApplicantDialog(null)}
                onMovedApplicant={toggleMoveApplicantDialog(null)}
                jobApplicantId={selectedApplicantId}
                accountId={accountId}
              />
            )}
            {shareApplicantDialogVisible && selectedApplicantJobId && (
              <ShareApplicantDialog
                key={selectedApplicantId}
                jobApplicantId={selectedApplicantId}
                jobId={selectedApplicantJobId}
                onClose={toggleShareApplicantDialog(null, null)}
                onSharedApplicant={toggleShareApplicantDialog(null, null)}
              />
            )}
          </>
        ) : null}
        {addApplicantDialogVisible && (
          <AddApplicantDialog
            jobs={jobs || []}
            onClose={toggleAddApplicantDialog}
            isSmallScreen={isSmallScreen}
          />
        )}
        {exportApplicantDialogVisible && (
          <ExportApplicantsDialog
            orgs={orgs || []}
            orgId={orgIdForExport}
            jobId={filterByJobId ? parseInt(filterByJobId, 10) : null}
            onClose={toggleExportApplicantDialog}
            statusFilter={filterByStatus || undefined}
            locationFilter={filterByLocation || undefined}
            nameOrPhoneNumberFilter={filterByNameOrPhoneNumber || undefined}
            includeRejectedExpired={!!includeRejectedExpired}
          />
        )}
      </div>
    </div>
  );
};
